import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, gridClasses, GridColDef } from "@mui/x-data-grid";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LightTooltip } from "../../../../../../components/LightTooltip";
import { formatNumber } from "../../../../../../utils/numberUtils";
import { lightScrollbarStyle } from "../../../../../../styles/lightScrollbarStyle";
import { EmptyState } from "../../../../components/EmptyState";
import { FaiByLocationModal } from "./FaiByLocationModal";

interface FaiProduct {
  productId: number;
  productName: string;
  signalStrength: number;
}

interface FaiByDepartmentTableProps {
  data?: FaiProduct[];
  loading: boolean;
  departmentName?: string;
  roleName?: string;
}

type SkeletonRow = { id: string; isSkeleton: true };
type DataRow = FaiProduct & { id: string; isSkeleton: false };
type TableRow = DataRow | SkeletonRow;

const HeaderWithTooltip: React.FC<{
  title: string;
  tooltip?: string;
}> = React.memo(({ title, tooltip }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="subtitle2" fontWeight="bold">
        {title}
      </Typography>
      {tooltip && (
        <LightTooltip
          title={<Typography variant="body2">{tooltip}</Typography>}
        >
          <IconButton size="small" sx={{ p: 0 }}>
            <HelpOutlineIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          </IconButton>
        </LightTooltip>
      )}
    </Stack>
  );
});

HeaderWithTooltip.displayName = "HeaderWithTooltip";

export const FaiByDepartmentTable: React.FC<FaiByDepartmentTableProps> =
  React.memo(({ data, loading, departmentName, roleName }) => {
    const [locationModalOpen, setLocationModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<FaiProduct | null>(
      null
    );

    const maxSignalStrength = useMemo(() => {
      if (!data || data.length === 0) return 0;
      return Math.max(...data.map((item) => item.signalStrength));
    }, [data]);

    const hasResults = Boolean(data && data.length > 0);
    const showEmptyState = !loading && !hasResults;

    const emptyStateDescription = useMemo(() => {
      if (departmentName && roleName && roleName !== "any") {
        return `No signal strength data found for ${departmentName} department with ${roleName} role. Try selecting a different role or department.`;
      } else if (departmentName) {
        return `No signal strength data found for ${departmentName} department. Try selecting a different department.`;
      }
      return "No signal strength data found. Please select a department.";
    }, [departmentName, roleName]);

    const rows: TableRow[] = useMemo(() => {
      if (loading) {
        return Array.from({ length: 10 }).map(
          (_, index) =>
            ({
              id: `skeleton-${index}`,
              isSkeleton: true,
            }) as SkeletonRow
        );
      }
      return (data?.map(
        (item: FaiProduct) =>
          ({
            ...item,
            id: `${item.productId}`,
            isSkeleton: false,
          }) as DataRow
      ) || []) as TableRow[];
    }, [data, loading]);

    const columnsDefinition: GridColDef<TableRow>[] = useMemo(
      () => [
        {
          field: "productName",
          headerName: "Products",
          flex: 1,
          sortable: false,
          renderHeader: () => <HeaderWithTooltip title="Products" />,
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="80%" />;
            }
            const rowData = params.row as DataRow;
            return (
              <Tooltip title={rowData.productName} placement="bottom-start">
                <Typography variant="body2" noWrap>
                  {rowData.productName}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          field: "signalStrength",
          headerName: "Signal Strength",
          flex: 1,
          width: 200,
          sortable: false,
          renderHeader: () => (
            <HeaderWithTooltip
              title="Signal Strength"
              tooltip="Signal Strength is the frequency at which we can determine the usage of a product within a department"
            />
          ),
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  width="100%"
                >
                  <Skeleton variant="text" width="60px" />
                  <Skeleton variant="rectangular" width="100%" height={4} />
                </Stack>
              );
            }
            const rowData = params.row as DataRow;
            const percentage =
              maxSignalStrength > 0
                ? (rowData.signalStrength / maxSignalStrength) * 100
                : 0;

            return (
              <Stack
                direction="row"
                alignItems="center"
                width="100%"
                spacing={1}
              >
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ minWidth: "40px" }}
                >
                  {formatNumber(rowData.signalStrength)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={percentage}
                  sx={{
                    width: "66%",
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: "#e1f5fe",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#1976d2",
                      borderRadius: 3,
                    },
                  }}
                />
              </Stack>
            );
          },
        },
        {
          field: "locations",
          headerName: "Locations",
          width: 150,
          sortable: false,
          renderHeader: () => <HeaderWithTooltip title="Locations" />,
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="80%" />;
            }
            return (
              <Link
                component="button"
                variant="body2"
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "none",
                  },
                }}
                onClick={() => {
                  const rowData = params.row as DataRow;
                  setSelectedProduct(rowData);
                  setLocationModalOpen(true);
                }}
              >
                View Top Locations
              </Link>
            );
          },
        },
      ],
      [maxSignalStrength]
    );

    const titleText = useMemo(() => {
      if (!departmentName) return "";
      if (hasResults) {
        return `Top 10 ${departmentName} products found`;
      }
      return `0 ${departmentName} products found`;
    }, [departmentName, hasResults]);

    return (
      <Box>
        {departmentName && <Typography variant="h5">{titleText}</Typography>}

        {showEmptyState ? (
          <EmptyState
            title="No signal strength data found"
            description={emptyStateDescription}
            height={600}
          />
        ) : (
          <Box sx={{ height: 520, width: "100%", marginLeft: -1 }}>
            <DataGrid
              rows={rows}
              columns={columnsDefinition}
              hideFooter
              rowHeight={45}
              getRowId={(row) => row.id}
              disableColumnFilter
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnResize
              disableColumnSelector
              sx={{
                [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: "none",
                  },
                [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                  {
                    outline: "none",
                  },
                [`& .${gridClasses.columnSeparator}`]: {
                  [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
                    display: "none",
                  },
                },
                "& .MuiDataGrid-columnHeader": {
                  borderBottom: "none !important",
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer":
                  {
                    "& .MuiIconButton-root": {
                      padding: 0,
                    },
                    "& .MuiTouchRipple-root": {
                      display: "none",
                    },
                  },
                "&, [class^=MuiDataGrid]": { border: "none" },
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-filler": {
                  display: "none",
                },
                ".MuiDataGrid-scrollbarFiller": {
                  display: "none",
                },
                "& .MuiDataGrid-scrollbar--vertical": {
                  ...lightScrollbarStyle,
                },
                "& .MuiDataGrid-row": {
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                },
              }}
            />
          </Box>
        )}

        {selectedProduct && (
          <FaiByLocationModal
            open={locationModalOpen}
            onClose={() => setLocationModalOpen(false)}
            product={{
              id: selectedProduct.productId,
              name: selectedProduct.productName,
            }}
            department={departmentName}
            role={roleName !== "any" ? roleName : undefined}
          />
        )}
      </Box>
    );
  });

FaiByDepartmentTable.displayName = "FaiByDepartmentTable";
